import { ReactComponent as Approved } from "@/assets/images/approved-icon.svg"
import { ReactComponent as CrossSmall } from "@/assets/images/cross-small.svg"
import { ReactComponent as NoPerson } from "@/assets/images/no-person.svg"
import { ReactComponent as ReportSent } from "@/assets/images/report-sent.svg"
import { ReactComponent as SessionCancelled } from "@/assets/images/session-cancelled.svg"
import { DataContext, ScheduleContext } from "@/contexts"
import { className } from "@/helpers/className"
import { formatDateKeepZone, minutesToHours } from "@/helpers/dates"
import { buildId } from "@/helpers/forms"
import { IconButton } from "@mui/material"
import moment from "moment"
import React, { forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { MeetingFormatTitles, SessionActions, SessionStatuses, SessionTitles } from "../../constants"
import { checkActionExist } from "../../helpers"
import { ChangeStatusDialog } from "../change_status"
import { NewDeleteSessionDialog } from "../new_delete_session"
import { ReactComponent as Check } from "../../../../assets/images/check1.svg"

const SessionContent = ({ meetingFormat, duration, status, hasAssignment, children }) => {
  const [hours, minutes] = minutesToHours(duration)
  const contentClassName = useMemo(() => className("session-calendar-day_content", status && `-${status}`), [status])
  const lessonAssignedClassname = useMemo(() => className("session-calendar-lesson-indicator", !hasAssignment && `-not-assigned`), [hasAssignment])
  return (
    <div className={contentClassName}>
      <div className={lessonAssignedClassname}>
        {hasAssignment ? (
          <>
            <Check width="13" height="13" style={{ fill: 'white' }}/>
            <span className="ml-1">Assigned</span>
          </>
        ) : (
          <span>No lesson assigned</span>
        )}
      </div>
      <span className="session-calendar-day_content-duration">
        {hours}h {minutes}min
      </span>
      <span className="session-calendar-day_content-type">{MeetingFormatTitles[meetingFormat]}</span>
      {children}
    </div>
  )
}

const SessionHeaderIcon = ({ status, marked }) => {
  const icon = useMemo(() => {
    switch (status) {
      case SessionStatuses.Cancelled:
        return <NoPerson />
      case SessionStatuses.CancelledNoShow:
        return <SessionCancelled />
      case SessionStatuses.Finished: {
        return marked ? <ReportSent /> : <Approved />
      }
      default:
        return null
    }
  }, [status, marked])

  const iconWrapperClassName = useMemo(() => {
    let name = ""
    switch (status) {
      case SessionStatuses.Cancelled:
      case SessionStatuses.CancelledNoShow: {
        name = "icon-gray-color"
        break
      }
      case SessionStatuses.Finished: {
        name = "icon-green-color"
        break
      }
    }
    return className("session-status-icon", name)
  }, [status])

  return <div className={iconWrapperClassName}>{icon}</div>
}

const SessionNode = forwardRef(function SessionNode({ children, className: nodeClassName, onClick }, ref) {
  const nodeId = useMemo(buildId, [])
  return typeof onClick === "function" ? (
    <label htmlFor={nodeId} className={className(nodeClassName, "cursor-pointer")}>
      <button ref={ref} id={nodeId} type="button" className="d-none" onClick={onClick} />
      {children}
    </label>
  ) : (
    <div ref={ref} className={nodeClassName}>
      {children}
    </div>
  )
})

const Session = ({ session, className: htmlClass, onClick, children, actionHandlers }) => {
  const {
    short = false,
    showIcon = false,
    month,
    highlighted = new Set(),
    marked = new Set(),
  } = useContext(ScheduleContext)
  const { schedule } = useContext(DataContext)
  const [newDeleteDialog, setNewDeleteDialog] = useState(false)
  const { started_on, status, started_at, meeting_format, duration, assignment_type, session_type } = session
  const day = useMemo(() => moment(formatDateKeepZone(started_on)), [started_on])
  const isSameMonth = useMemo(() => (month ? day.isSame(moment(month), "month") : true), [day, month])
  const isHighlighted = useMemo(() => highlighted.has(started_on), [highlighted, started_on])
  const isMarked = useMemo(() => marked.has(started_on), [marked, started_on])

  const sessionClassName = useMemo(
    () =>
      className(
        "session-calendar-day",
        short ? "-month-view" : "-week-view",
        status && `-${status}`,
        !isSameMonth && "-not-current-month",
        isHighlighted && "-highlighted",
        htmlClass
      ),
    [htmlClass, isHighlighted, isSameMonth, short, status]
  )

  const dateClassName = useMemo(
    () => className("session-calendar-date", day.isSame(moment(), "day") && "-current"),
    [day]
  )

  const hasAssignment = useMemo(() =>
      (assignment_type === "Lesson" && session_type === "lesson") ||
      session_type === "icebreakers" ||
      session_type === "assessment",
    [assignment_type, session_type]
  );

  const actionsList = useMemo(() => {
    if (
      session.status === SessionStatuses.Pending &&
      checkActionExist(session.actions, SessionActions.Cancel) &&
      checkActionExist(session.actions, SessionActions.Destroy)
    ) {
      return session.actions.filter(action => action !== SessionActions.Destroy)
    }
    return session.actions
  }, [session.actions, session.status])

  const hasAction = useCallback(
    checkedAction =>
      checkActionExist(actionsList, checkedAction) && typeof actionHandlers[checkedAction] === "function",
    [actionsList, actionHandlers]
  )

  return (
    <SessionNode className={sessionClassName} onClick={onClick}>
      {isMarked && <div className="session-calendar-date-flag" />}
      <div className="d-flex align-items-center">
        <div className={"d-flex w-100 justify-content-between align-items-center"}>
          <span className={dateClassName}>{day.format("DD")}</span>
          {hasAction(SessionActions.Cancel) && (
            <IconButton
              onClick={() => {
                setNewDeleteDialog(true)
              }}
              className="p-0"
            >
              <CrossSmall />
            </IconButton>
          )}
        </div>
        {showIcon && <SessionHeaderIcon status={status} marked={isMarked} />}
      </div>
      <div className="session-calendar-day-time">
        {formatDateKeepZone(started_at, "hh:mm a")} <strong>{schedule.time_zone_abbr}</strong>
      </div>
      <div className="d-flex flex-column flex-grow-1 justify-content-between">
        <SessionContent meetingFormat={meeting_format} duration={duration} status={status} hasAssignment={hasAssignment} />
        {!short && children}
      </div>
      <NewDeleteSessionDialog show={newDeleteDialog} onClose={() => setNewDeleteDialog(false)} session={session} />
    </SessionNode>
  )
}

const NodeTypePicker = forwardRef(function NodeTypePicker(props, ref) {
  return typeof props.onClick === "function" ? <button {...props} ref={ref} /> : <div {...props} ref={ref} />
})

const SessionStatusPicker = ({ session }) => {
  const status = session && session.status
  const contentClassName = useMemo(() => className("session-calendar-day_content", status && `-${status}`), [status])
  const wrapperRef = useRef(null)
  const pickerRef = useRef(null)
  const listRef = useRef(null)
  const [open, setOpen] = useState(false)

  const openMenu = () => {
    setOpen(true)
    setOverflow("hidden")
  }

  const setOverflow = useCallback(value => document.body.parentElement.style.setProperty("overflow", value), [])

  const closeMenu = useCallback(() => {
    setOpen(false)
    pickerRef.current?.focus()
    setOverflow(null)
  }, [setOverflow])

  const onClickOutside = useCallback(
    event => {
      if (open && !wrapperRef.current?.contains(event.target)) closeMenu()
    },
    [open, closeMenu]
  )

  const onEscape = useCallback(
    event => {
      if (open && event.keyCode === 27) closeMenu()
    },
    [open, closeMenu]
  )

  useEffect(() => {
    if (open && listRef.current) listRef.current?.querySelector("button")?.focus()
  }, [open])

  useEffect(() => {
    document.addEventListener("keydown", onEscape)
    return () => {
      document.removeEventListener("click", onClickOutside)
      document.removeEventListener("keydown", onEscape)
    }
  }, [onClickOutside, onEscape])

  return (
    <div
      ref={wrapperRef}
      className={`${contentClassName} session-calendar-day_content-actions flex-column`}
      style={{
        gap: 0,
        gridGap: 0,
      }}
    >
      <span
        className="session-calendar-lesson-indicator mt-1"
        style={{
          fontSize: "0.78rem",
        }}
      >
        Status: {SessionTitles[session.status]}
      </span>
      <NodeTypePicker
        ref={pickerRef}
        onClick={openMenu}
        className="session-calendar-day_status gap-025 d-inline w-100"
        style={{ borderTop: 0, backgroundColor: "transparent" }}
      >
        <span
          className="session-calendar-day_content"
          style={{ color: "#3c00f5", fontSize: "0.76rem", fontWeight: "bold" }}
        >
          Change Status
        </span>
      </NodeTypePicker>
      <ChangeStatusDialog show={open} onClose={closeMenu} session={session} />
    </div>
  )
}

const SessionActionsList = ({ session, actionHandlers }) => {
  const executeHandler = useCallback(
    (event, action, params) => {
      const handler = actionHandlers[action]
      if (handler) handler(event, { action, session, params })
    },
    [actionHandlers, session]
  )

  const actionsList = useMemo(() => {
    if (
      session.status === SessionStatuses.Pending &&
      checkActionExist(session.actions, SessionActions.Cancel) &&
      checkActionExist(session.actions, SessionActions.Destroy)
    ) {
      return session.actions.filter(action => action !== SessionActions.Destroy)
    }
    return session.actions
  }, [session.actions, session.status])

  const hasAction = useCallback(
    checkedAction =>
      checkActionExist(actionsList, checkedAction) && typeof actionHandlers[checkedAction] === "function",
    [actionsList, actionHandlers]
  )

  switch (session.status) {
    default:
      return <SessionStatusPicker session={session} hasAction={hasAction} onChange={executeHandler} />
  }
}

export const SessionTile = ({ session, actionHandlers = {}, className, onClick }) => {
  return (
    <Session session={session} className={className} onClick={onClick} actionHandlers={actionHandlers}>
      <SessionActionsList session={session} actionHandlers={actionHandlers} />
    </Session>
  )
}
